<script lang="ts">
  /**
   * Homestead — the register of what the house is made of.
   *
   * v0 is deliberately pure CRUD with no AI: the spec is explicit that the value
   * here is knowing what you own, when it was installed and when it was last
   * serviced. A register nobody trusts is worse than no register, and trust comes
   * from the entries being correct rather than from them being clever.
   *
   * Sign-in, the API client and the shell are verbatim copies of Vault's, per the
   * programme convention — one sign-in works across every portal precisely because
   * every client asks the same project who you are.
   */
  import { Shell, Icon, type PortalLink , SignIn } from "@steyncd/steyn-design";
  import { api } from "./lib/api";
  import Assets from "./routes/Assets.svelte";
  import Jobs from "./routes/Jobs.svelte";
  import Chores from "./routes/Chores.svelte";
  import Seasons from "./routes/Seasons.svelte";
  import { session } from "./lib/stores.svelte";
  import { glance } from "./lib/glance.svelte";
  import { signIn, signOut, atLeast, completeRedirect, explainAuthError } from "./lib/auth";

  const FRONT_DOOR = import.meta.env.VITE_FRONTDOOR_URL ?? "https://www.helloeben.co.za";

  type Section = "assets" | "jobs" | "chores" | "seasons";
  let section = $state<Section>("assets");

  /**
   * Two audiences, one app.
   *
   * A `partner` gets the whole portal. A `display` — the kitchen tablet, signed
   * in once and never again — gets the chores board and nothing else, because
   * F6 says the balance has to be visible there or it stays a dispute, and
   * because the register is serials, suppliers and replacement values, which is
   * a shopping list for anyone who should not have it. The server enforces both
   * halves; this only decides what to draw.
   */
  const role = $derived(session.claims?.role ?? null);
  const isPartner = $derived(atLeast(role, "partner"));
  const isDisplay = $derived(atLeast(role, "display"));

  const NAV_FULL = [
    { id: "assets", label: "What we own", icon: "homestead" },
    { id: "jobs", label: "What needs doing", icon: "wrench" },
    // `chore` is a checklist, not a tick: `check` already means "live" in the
    // rail, and the two must not read as the same thing at 19px.
    { id: "chores", label: "Pocket money", icon: "chore" },
    { id: "seasons", label: "The year", icon: "calendar" },
  ];
  const nav = $derived(isPartner ? NAV_FULL : NAV_FULL.filter((n) => n.id === "chores"));

  // Derived rather than reset in an effect: the tablet has exactly one screen,
  // and forcing it here means no code path can navigate it somewhere it is not
  // allowed and then get a bare error from the API instead.
  const shown = $derived<Section>(isPartner ? section : "chores");

  const ICON: Record<string, string> = {
    ha: "house", hq: "finance", screening: "film", codebots: "bot",
    vault: "vault", homestead: "homestead", waypoint: "waypoint",
    hindsight: "hindsight", fabric: "fabric", frontdoor: "door",
  };

  async function loadPortals(): Promise<PortalLink[]> {
    const res = await api.get<{ portals: { id: string; name: string; url: string }[] }>(
      `${import.meta.env.VITE_FABRIC_API_URL}/portals`,
    );
    return res.portals
      .filter((p) => p.id !== "homestead")
      .map((p) => ({ id: p.id, name: p.name, url: p.url, icon: ICON[p.id] ?? "door" }));
  }

  let signingIn = $state(false);
  let signInError = $state<string | null>(null);

  /**
   * Finish a redirect sign-in before deciding what to render. Without this the
   * credential in the URL is never collected and the gate simply reappears.
   */
  let settling = $state(true);
  $effect(() => {
    void completeRedirect()
      .then((r) => {
        // A redirect that came back with a real error used to be swallowed, so the gate
        // just reappeared with nothing saying why. Surface it like any other failure.
        if (r.error) signInError = explainAuthError(r.error);
      })
      .finally(() => (settling = false));
  });

  async function doSignIn() {
    signingIn = true;
    signInError = null;
    try {
      await signIn();
    } catch (e) {
      const code = (e as { code?: string }).code ?? "";
      if (!/popup-closed-by-user|cancelled-popup-request/.test(code)) {
        // The code is shown, not swallowed. A bare "sign-in failed" has cost this
        // programme several debugging rounds; the code is the whole diagnosis.
        signInError = explainAuthError(e);
      }
    } finally {
      signingIn = false;
    }
  }
</script>

{#if !session.ready || settling}
  <div class="boot" aria-busy="true"></div>
{:else if !session.user}
  <SignIn
    product="Homestead"
    mark="homestead"
    tagline="What the house is made of, and what it needs next."
    onsignin={doSignIn}
    busy={signingIn}
    error={signInError}
    glance={glance.stats}
    siblings={["Home", "Finance", "Screening Room", "Vault", "Hindsight", "Codebots"]}
  />
{:else if !isDisplay}
  <div class="gate">
    <div class="card--hero gate__card">
      <h1>Homestead</h1>
      <p class="sub">
        {#if session.claims}
          This portal needs a household account. You are signed in as {session.claims.role}.
        {:else}
          This Google account has no access to the Steyn household.
        {/if}
      </p>
      <a class="btn-primary gate__btn" href={FRONT_DOOR}>Go to the Front Door</a>
      <button class="gate__link" onclick={signOut}>Sign out</button>
    </div>
  </div>
{:else}
  <!-- Homestead is a home surface, not an operator one: open spacing, serif figures,
       52px rows. A household reads this, not someone on shift. -->
  <Shell
    product="Homestead"
    mark="homestead"
    surface="home"
    portals={isPartner ? loadPortals : null}
    {nav}
    active={shown}
    onnavigate={(id) => (section = id as Section)}
    frontDoorUrl={FRONT_DOOR}
  >
    {#snippet header()}
      <span class="who">
        <Icon name="user" size={14} />
        {session.user?.displayName ?? session.user?.email}
      </span>
      <!-- No sign-out on the tablet. It is signed in once, by an adult, and a
           child who taps this locks the kitchen screen out of the house until
           somebody notices. There is nothing on a display session worth
           protecting with a sign-out button. -->
      {#if isPartner}
        <button class="signout" onclick={signOut} aria-label="Sign out">
          <Icon name="logout" size={16} />
        </button>
      {/if}
    {/snippet}

    {#if shown === "assets"}
      <Assets />
    {:else if shown === "jobs"}
      <Jobs />
    {:else if shown === "seasons"}
      <Seasons />
    {:else}
      <!-- Confirming is what moves money, so it is a parent's alone. The server
           refuses it for a display session too — this only stops the button
           being drawn for someone who cannot use it. -->
      <Chores canConfirm={isPartner} />
    {/if}
  </Shell>
{/if}

<style>
  .boot { min-height: 100vh; background: var(--aurora); }
  .gate {
    min-height: 100vh;
    background: var(--aurora);
    display: grid;
    place-items: center;
    padding: 24px;
  }
  .gate__card { width: min(380px, 100%); padding: 30px 28px; text-align: center; }
  h1 { margin: 0 0 6px; font-size: 22px; font-weight: 800; letter-spacing: -0.5px; color: var(--tx); }
  .gate__btn { display: inline-block; margin-top: 18px; padding: 9px 18px; font-size: 12.5px; }
  .gate__link { display: block; margin: 12px auto 0; font-size: 11.5px; font-weight: 700; color: var(--mut); }
  .who { display: inline-flex; align-items: center; gap: 6px; font-size: 11.5px; font-weight: 700; color: var(--mut); }
  .signout { padding: 6px; border-radius: var(--r-control); color: var(--mut); }
  .signout:hover { color: var(--acc); }
</style>
